import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const TestRide = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedProductId, setSelectedProductId] = useState(id || '');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    location: '',
    message: ''
  });

  const API_URL = 'https://janvaahan.com/backend/public/api';

  useEffect(() => {
    fetchData();
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [id, user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all products for dropdown
      const productsResponse = await axios.get(`${API_URL}/products`);
      setProducts(productsResponse.data);
      
      // If specific product ID is provided, fetch that product
      if (id) {
        const productResponse = await axios.get(`${API_URL}/products/${id}`);
        setProduct(productResponse.data);
        setSelectedProductId(id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleProductChange = (e) => {
    setSelectedProductId(e.target.value);
    // Find and set the selected product
    const selectedProduct = products.find(p => p.id.toString() === e.target.value);
    setProduct(selectedProduct);
  };

  const validateForm = () => {
    if (!selectedProductId) {
      setError('Please select a product to test ride');
      return false;
    }
    if (!formData.name.trim()) {
      setError('Please enter your full name');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Please enter your phone number');
      return false;
    }
    if (!/^[0-9]{10}$/.test(formData.phone)) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    if (!formData.preferred_date) {
      setError('Please select a preferred date');
      return false;
    }
    if (!formData.preferred_time) {
      setError('Please select a preferred time');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      const testRideData = {
        product_id: parseInt(selectedProductId),
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        preferred_date: formData.preferred_date,
        preferred_time: formData.preferred_time,
        location: formData.location,
        message: formData.message
      };

      console.log('Submitting test ride:', testRideData);

      const response = await axios.post(`${API_URL}/test-rides`, testRideData, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      console.log('Test ride response:', response.data);

      setSuccess('Test ride booked successfully! We will contact you shortly to confirm your appointment.');
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        phone: '',
        preferred_date: '',
        preferred_time: '',
        location: '',
        message: ''
      });

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (error) {
      console.error('Error booking test ride:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 401) {
        setError('Please login to book a test ride');
        setTimeout(() => navigate('/login'), 2000);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to book test ride. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Get minimum date for date picker (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Book a Test Ride
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Experience the thrill of electric mobility firsthand. Schedule your test ride today!
          </p>
        </div>

        {success && (
          <div className="mb-8 bg-green-100 border-l-4 border-green-500 text-green-700 px-4 py-3 rounded shadow-md">
            <div className="flex items-center">
              <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{success}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-8 bg-red-100 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded shadow-md">
            <div className="flex items-center">
              <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <h2 className="text-2xl font-bold mb-6">Schedule Your Test Ride</h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Product Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Bike Model *
                </label>
                <select
                  value={selectedProductId}
                  onChange={handleProductChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                >
                  <option value="">-- Select a bike model --</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} - {product.price}
                    </option>
                  ))}
                </select>
                {product && (
                  <p className="text-xs text-gray-500 mt-1">
                    You're testing: {product.name} ({product.model})
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="10-digit mobile number"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    name="preferred_date"
                    value={formData.preferred_date}
                    onChange={handleChange}
                    min={getMinDate()}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Time *
                  </label>
                  <select
                    name="preferred_time"
                    value={formData.preferred_time}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  >
                    <option value="">Select time</option>
                    <option value="09:00">09:00 AM - 10:00 AM</option>
                    <option value="10:00">10:00 AM - 11:00 AM</option>
                    <option value="11:00">11:00 AM - 12:00 PM</option>
                    <option value="12:00">12:00 PM - 01:00 PM</option>
                    <option value="14:00">02:00 PM - 03:00 PM</option>
                    <option value="15:00">03:00 PM - 04:00 PM</option>
                    <option value="16:00">04:00 PM - 05:00 PM</option>
                    <option value="17:00">05:00 PM - 06:00 PM</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="City or dealership location"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Message (Optional)
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Any specific requirements or questions?"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Booking...
                  </span>
                ) : (
                  'Book Test Ride'
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                By booking a test ride, you agree to our{" "}
                <Link to="/terms" className="text-red-600 hover:underline">Terms of Service</Link> and{" "}
                <Link to="/privacy" className="text-red-600 hover:underline">Privacy Policy</Link>
              </p>
            </form>
          </div>

          {/* Info Section */}
          <div className="space-y-6">
            {/* Product Info (if specific product) */}
            {product && (
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold mb-4">You're Test Riding</h3>
                <div className="flex items-center space-x-4">
                  <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                    <img
                      src={product.image_url?.startsWith('http') ? product.image_url : `https://janvaahan.com/backend/public${product.image_url}`}
                      alt={product.name}
                      className="h-20 object-contain"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/80?text=Bike';
                      }}
                    />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold">{product.name}</h4>
                    <p className="text-gray-600 text-sm">{product.model}</p>
                    <p className="text-red-600 font-bold mt-1">{product.price}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Range:</span>
                      <span className="ml-2 font-medium">{product.range || 80} km</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Top Speed:</span>
                      <span className="ml-2 font-medium">{product.top_speed || 75} km/h</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Why Test Ride */}
            <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl shadow-xl p-6 text-white">
              <h3 className="text-xl font-bold mb-4">Why Take a Test Ride?</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-2xl mr-3">✓</span>
                  <span>Experience the power and performance firsthand</span>
                </li>
                <li className="flex items-start">
                  <span className="text-2xl mr-3">✓</span>
                  <span>Feel the comfort and handling</span>
                </li>
                <li className="flex items-start">
                  <span className="text-2xl mr-3">✓</span>
                  <span>Get expert guidance from our team</span>
                </li>
                <li className="flex items-start">
                  <span className="text-2xl mr-3">✓</span>
                  <span>No commitment, just experience</span>
                </li>
              </ul>
            </div>

            {/* Testimonials */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold mb-4">What Others Say</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex text-yellow-400 mb-2">★★★★★</div>
                  <p className="text-gray-600 text-sm">"Amazing experience! The test ride convinced me to buy immediately."</p>
                  <p className="text-xs text-gray-400 mt-1">- Vikram S.</p>
                </div>
                <div>
                  <div className="flex text-yellow-400 mb-2">★★★★★</div>
                  <p className="text-gray-600 text-sm">"Great support and guidance during the test ride. Highly recommended!"</p>
                  <p className="text-xs text-gray-400 mt-1">- Neha P.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestRide;